import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useSession } from "@/components/layout/AppShell";
import { ws } from "@/lib/ws";
import type { CredentialMeta } from "@shared/protocol";
import { relativeTime } from "@/lib/utils";

const GITHUB_APP_SLUG =
  (import.meta as ImportMeta & { env: Record<string, string> }).env
    .VITE_GITHUB_APP_SLUG ?? "";

const GITLAB_ISSUER = "https://gitlab.com";

/** GitLab's own id_token `sub` claim shape — see internal/api's
 *  gitlabOidc.ts, which builds this EXACT string from the token's
 *  verified project_path/ref_type/ref claims to look up a credential row
 *  by exact match. Constructed identically here so what an admin enters
 *  in this form is what a real CI job's token will actually produce. */
function gitlabSubjectClaim(projectPath: string, branch: string): string {
  return `project_path:${projectPath}:ref_type:branch:ref:${branch}`;
}

function GitLabConnectionRow({
  cred,
  onRevoke,
}: {
  cred: CredentialMeta;
  onRevoke: (id: string) => void;
}) {
  // Un-does gitlabSubjectClaim() for display — best-effort, falls back to
  // the raw string if it doesn't match the expected shape (e.g. a
  // ref_type:tag connection made via the raw Credentials page instead of
  // this form).
  const m = /^project_path:(.+):ref_type:branch:ref:(.+)$/.exec(
    cred.subject_claim,
  );
  return (
    <TableRow>
      <TableCell className="font-mono text-sm">
        {m ? m[1] : cred.subject_claim}
      </TableCell>
      <TableCell className="font-mono text-sm text-muted-foreground">
        {m ? m[2] : "—"}
      </TableCell>
      <TableCell className="text-sm">{cred.name}</TableCell>
      <TableCell
        className="font-mono text-xs text-muted-foreground"
        title="--account / $PURE_ACCOUNT_ID for this connection"
      >
        {cred.created_by}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {cred.last_used ? `used ${relativeTime(cred.last_used)}` : "never used"}
      </TableCell>
      <TableCell>
        {cred.revoked_at ? (
          <Badge variant="secondary">Revoked</Badge>
        ) : (
          <Button variant="outline" size="sm" onClick={() => onRevoke(cred.id)}>
            Revoke
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function GitLabIntegrationCard({
  orgId,
  userId,
}: {
  orgId: string;
  userId: string;
}) {
  const [credentials, setCredentials] = useState<CredentialMeta[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [projectPath, setProjectPath] = useState("");
  const [branch, setBranch] = useState("main");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const offState = ws.on("creds.state", (m) => setCredentials(m.credentials));
    const offCreated = ws.on("creds.created", (m) => {
      setCredentials((prev) => [m.credential, ...prev]);
      setShowForm(false);
      setProjectPath("");
      setBranch("main");
      setName("");
      setError(null);
      setSubmitting(false);
    });
    const offRevoked = ws.on("creds.revoked", (m) => {
      setCredentials((prev) =>
        prev.map((c) => (c.id === m.id ? { ...c, revoked_at: Date.now() } : c)),
      );
    });
    const offErr = ws.on("error", (m) => {
      if (m.req_type.startsWith("creds.")) {
        setError(m.message);
        setSubmitting(false);
      }
    });
    ws.send({ type: "creds.list" });
    return () => {
      offState();
      offCreated();
      offRevoked();
      offErr();
    };
  }, []);

  const gitlabCredentials = credentials.filter((c) => c.provider === "gitlab");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!projectPath.trim() || !branch.trim()) return;
    setError(null);
    setSubmitting(true);
    ws.send({
      type: "creds.create",
      name: name.trim() || `${projectPath.trim()}@${branch.trim()}`,
      provider: "gitlab",
      oidc_issuer: GITLAB_ISSUER,
      subject_claim: gitlabSubjectClaim(projectPath.trim(), branch.trim()),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>GitLab CI</CardTitle>
        <CardDescription>
          Let a GitLab CI/CD pipeline call the Pure compile API directly —
          authenticated by GitLab&apos;s own short-lived <code>id_token</code>,
          no API key stored in GitLab at all.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription>
            A connection belongs to <strong>you</strong> (user ID{" "}
            <code className="font-mono">{userId}</code>), and bills to{" "}
            <strong>this organization</strong> (org ID{" "}
            <code className="font-mono">{orgId}</code>
            ). A GitLab pipeline authenticating with an <code>
              id_token
            </code>{" "}
            must pass both — Pure doesn&apos;t infer either one (the same GitLab
            project/branch can be connected by more than one person, and by more
            than one org), so the pipeline says exactly which connection and
            which bill it means. See the CI snippet below.
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {gitlabCredentials.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {gitlabCredentials.map((c) => (
                <GitLabConnectionRow
                  key={c.id}
                  cred={c}
                  onRevoke={(id) => ws.send({ type: "creds.revoke", id })}
                />
              ))}
            </TableBody>
          </Table>
        )}

        {showForm ? (
          <form onSubmit={submit} className="space-y-3 max-w-sm">
            <div className="space-y-1">
              <Label htmlFor="gl-project">GitLab project path</Label>
              <Input
                id="gl-project"
                placeholder="mygroup/myproject"
                value={projectPath}
                onChange={(e) => setProjectPath(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="gl-branch">Branch</Label>
              <Input
                id="gl-branch"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Must be a <strong>protected</strong> branch in GitLab (Settings
                → Repository → Protected branches) — GitLab only stamps{" "}
                <code>ref_protected: true</code> onto the id_token for protected
                branches, and Pure requires that.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="gl-name">Name (optional)</Label>
              <Input
                id="gl-name"
                placeholder={
                  projectPath && branch ? `${projectPath}@${branch}` : undefined
                }
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Connecting…" : "Connect"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <Button onClick={() => setShowForm(true)}>
            Connect a GitLab project
          </Button>
        )}

        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">
            .gitlab-ci.yml setup, once connected
          </summary>
          <pre className="mt-2 p-3 bg-muted rounded-md text-xs overflow-x-auto">
            {`compile:
  id_tokens:
    PURE_ID_TOKEN:
      aud: https://api.pure.dev
  script:
    - curl -fsSL https://apt.pure.dev/install.sh | sh
    - pure build src/main.ts \\
        --oidc-token "$PURE_ID_TOKEN" \\
        --account ${userId} --org ${orgId}`}
          </pre>
          <p className="mt-2 text-xs text-muted-foreground">
            No API key anywhere in GitLab — <code>PURE_ID_TOKEN</code> is minted
            fresh by GitLab for this one job. <code>--account</code> (or{" "}
            <code>$PURE_ACCOUNT_ID</code>) is your user ID — it identifies which
            connection to check the token against. <code>--org</code> (or{" "}
            <code>$PURE_ORG_ID</code>) is the org that connection bills to —
            Pure cross-checks it against the connection's own org, so a mismatch
            fails loudly instead of silently billing somewhere unexpected. Both
            are required whenever
            <code>--oidc-token</code>/<code>$PURE_OIDC_TOKEN</code> is used. If
            someone else set up this connection, use their user ID from the{" "}
            <strong>Account</strong> column above, not yours.
          </p>
        </details>
      </CardContent>
    </Card>
  );
}

export function IntegrationsPage() {
  const [params] = useSearchParams();
  const installed = params.get("installed") === "1";
  const error = params.get("error");
  const { user, org, orgs, flags } = useSession();

  // Sidebar hides this page's nav entry unless the flag is on or you're a
  // super_admin (see Sidebar.tsx), but that alone doesn't stop someone
  // typing /integrations directly — guard the page itself too.
  const isSuperAdmin = orgs.some((o) => o.role === "super_admin");
  if (!flags.integrations_page && !isSuperAdmin) {
    return (
      <div className="max-w-4xl">
        <Alert>
          <AlertDescription>
            This feature isn&apos;t available yet.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const installUrl = GITHUB_APP_SLUG
    ? `https://github.com/apps/${GITHUB_APP_SLUG}/installations/new?state=${encodeURIComponent(org.id)}`
    : null;

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Integrations</h1>
        <p className="text-sm text-muted-foreground">
          Connect Pure to your git host so we can clone private repos at compile
          time.
        </p>
      </div>

      {installed && (
        <Alert variant="success">
          <AlertDescription>
            GitHub App installed successfully.
          </AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>GitHub install failed: {error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>GitHub</CardTitle>
          <CardDescription>
            Install the Pure GitHub App to grant access to selected
            repositories. Tokens are short-lived (1h), auto-rotating, and scoped
            to just the repos you choose.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {installUrl ? (
            <a
              href={installUrl}
              className="inline-flex items-center h-9 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Install Pure on GitHub
            </a>
          ) : (
            <Alert>
              <AlertDescription>
                GitHub App is not configured on this deployment. Set{" "}
                <code>VITE_GITHUB_APP_SLUG</code> in the frontend build
                environment.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <GitLabIntegrationCard orgId={org.id} userId={user.id} />
    </div>
  );
}
